'use client'

import {Card, CardContent} from "@/components/ui/card";
import DevPlaner from "@/assets/logo.png"
import Hero from "@/assets/login_hero.png"
import Image from "next/image";
import {Input} from "@/components/ui/input";
import {Button} from "@/components/ui/button";
import {useRouter} from "next/navigation";
import {useEffect, useState} from "react";
import {useAuth} from "@/hooks/useAuth";
import {AuthApiError} from "@/lib/auth";
import {Toast} from "@/lib/toast";
import {AuthOverlayLoader} from "@/components/ui/AuthOverlayLoader";

export default function SignUpPage() {
    const [email, setEmail] = useState<string>("");
    const [displayName, setDisplayName] = useState<string>("");
    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [passwordConfirm, setPasswordConfirm] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isBootstrapping, setIsBootstrapping] = useState<boolean>(true);

    const router = useRouter()
    const {register, loadMe} = useAuth()

    useEffect(() => {
        loadMe()
            .then((payload) => {
                if (!payload) {
                    return;
                }
                if (payload.currentWorkspace && !payload.workspaceSelectionRequired) {
                    router.replace("/dashboard");
                    return;
                }
                router.replace("/select-workspace?force=true");
            })
            .finally(() => {
                setIsBootstrapping(false);
            });
    }, [loadMe, router]);

    const onRegister = async () => {
        if (isLoading) {
            return;
        }

        if (password.trim().length < 8) {
            Toast.error("Passwort muss mindestens 8 Zeichen lang sein");
            return;
        }

        if (password !== passwordConfirm) {
            Toast.error("Passwörter stimmen nicht überein");
            return;
        }

        setIsLoading(true);
        try {
            await register(email, password, displayName, firstName || undefined, lastName || undefined);
            Toast.success("Registrierung erfolgreich");
            router.push("/login");
        } catch (e: unknown) {
            if (e instanceof AuthApiError) {
                Toast.error(e.payload?.message ?? "Registrierung fehlgeschlagen");
            } else {
                Toast.error("Registrierung fehlgeschlagen");
            }
        } finally {
            setIsLoading(false);
        }
    }

    if (isBootstrapping) {
        return <AuthOverlayLoader open={true} text={"Lade Benutzerdaten..."} />;
    }

    return (
        <Card className={"self-center my-auto max-w-5xl"}>
            <CardContent className={"grid grid-cols-2 gap-5 p-0"}>
                <div className={"flex flex-col p-14"}>
                    <div className={"flex gap-5 items-center font-bold"}>
                        <Image src={DevPlaner} alt={"DevPlaner"} className={"w-5 h-5"} />
                        <span>
                            DevPlaner
                        </span>
                    </div>
                    <div className={"mt-10 text-3xl font-bold flex flex-col gap-2"}>
                        <h1>
                            Hallo,
                        </h1>
                        <h2>
                            Schön dass du da bist!
                        </h2>
                    </div>
                    <p className={"mt-4 text-muted-foreground text-sm"}>
                        Erstelle hier dein DevPlaner Konto
                    </p>
                    <div className={"space-y-2 mt-10"}>
                        <Input type={"email"} id={"email"} placeholder={"E-Mail Adresse"} value={email} onChange={(e) => setEmail(e.target.value)} />
                        <Input type={"text"} id={"displayName"} placeholder={"Anzeigename"} value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
                        <div className={"grid grid-cols-2 gap-2"}>
                            <Input type={"text"} id={"firstName"} placeholder={"Vorname"} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                            <Input type={"text"} id={"lastName"} placeholder={"Nachname"} value={lastName} onChange={(e) => setLastName(e.target.value)} />
                        </div>
                        <Input type={"password"} id={"password"} placeholder={"Passwort"} value={password} onChange={(e) => setPassword(e.target.value)} />
                        <Input type={"password"} id={"passwordConfirm"} placeholder={"Passwort wiederholen"} value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} />
                    </div>
                    <Button
                        variant={"default"}
                        onClick={onRegister}
                        disabled={isLoading}
                        className={"mt-6 self-start"}
                    >
                        {isLoading ? "Lädt..." : "Registrieren"}
                    </Button>
                    <div className={"mt-6 text-xs flex items-center gap-2 text-placeholder"}>
                        <span>
                            Bereits ein Konto?
                        </span>
                        <Button
                            variant={"link"}
                            onClick={() => router.push("/login")}
                            className={"p-0 text-xs"}
                        >
                            Zur Anmeldung
                        </Button>
                    </div>
                </div>
                <Image src={Hero} alt={"Login Hero"} className={"w-full object-cover rounded-xl my-auto"} priority={true} />
            </CardContent>
        </Card>
    )
}
